import { isInternal, isProxyUser } from '@ecosystem/newron-client-lib';
import { User } from '@ecosystem/newron-client-lib/build/authentication/types';

import {
  AccessLevel,
  AvailableInteractions,
  Client,
  CollectionListItem,
  CollectionType,
  Interaction,
  InteractionElement,
  InteractionState,
} from './types';

export const TemplateAdminRole = 'GFKNEWRON_PLATFORM_TEMPLATE_ADMIN';

export const isTemplateAdmin = (user?: User): boolean => {
  if (!user) {
    return false;
  }
  return user.groups && user.groups.includes(TemplateAdminRole);
};

export const isEnabledAnywhere = (interaction: Partial<Record<InteractionElement, InteractionState>>): boolean =>
  Object.keys(interaction).some(key => interaction[key as InteractionElement] === 'enabled');

export const isEnabled = (interaction: InteractionState): boolean => interaction === 'enabled';
export const isDisabled = (interaction: InteractionState): boolean => interaction === 'disabled';
export const isVisible = (interaction: InteractionState): boolean =>
  interaction === 'enabled' || interaction === 'disabled';

const getShareCollectionInteraction = (
  isClientUser: boolean,
  canAssignClient: boolean,
  hasAssignedClients: boolean
): Partial<Record<InteractionElement, InteractionState>> =>
  canAssignClient
    ? { detailsButton: hasAssignedClients ? 'enabled' : 'disabled' }
    : isClientUser
      ? { detailsButton: 'enabled' }
      : {};

const getCreateTemplateInteraction = (
  isTemplateAdminUser: boolean,
  collectionType: CollectionType
): Partial<Record<InteractionElement, InteractionState>> =>
  isTemplateAdminUser && collectionType === CollectionType.Collection
    ? { overviewMenu: 'enabled', detailsMenu: 'enabled' }
    : {};

export const buildEmptyInteractions = (): AvailableInteractions =>
  Object.values(Interaction).reduce((acc, interaction) => ({ ...acc, [interaction]: {} }), {} as AvailableInteractions);

const disableAllInteractions = (interactions: AvailableInteractions): AvailableInteractions => {
  Object.keys(interactions).forEach(interaction => {
    Object.keys(interactions[interaction]).forEach(element => {
      interactions[interaction][element] = 'disabled';
    });
  });
  return interactions;
};

const getOwnerInteractions = (
  collection?: Pick<CollectionListItem, 'accessLevel' | 'assignedClients' | 'type'>,
  user?: User,
  clients?: Client[]
): AvailableInteractions => {
  const isTemplateAdminUser = isTemplateAdmin(user);
  const isInternalUser = isInternal(user);
  const isClientUser = !!user && !isInternalUser && !isProxyUser(user);
  const hasAssignedClients = collection?.assignedClients && collection.assignedClients.length > 0;
  const canAssignClient =
    collection.type === CollectionType.Collection &&
    (isInternalUser || hasAssignedClients || !!(clients && clients.length > 0));

  return {
    EditCollection: { overviewMenu: 'enabled', detailsButton: 'enabled' },
    DuplicateCollection: { overviewMenu: 'enabled', detailsMenu: 'enabled' },
    CreateTemplate: getCreateTemplateInteraction(isTemplateAdminUser, collection.type),
    DeleteCollection: { overviewMenu: 'enabled', detailsMenu: 'enabled' },
    RemovePermissions: {},
    ExportCollection: { detailsButton: 'enabled' },
    EditInsight: { detailsButton: 'enabled' },
    DeleteInsight: { detailsButton: 'enabled' },
    ExportInsight: { detailsButton: 'enabled' },
    CopyInsight: { detailsButton: 'enabled' },
    ShareCollection: getShareCollectionInteraction(isClientUser, canAssignClient, hasAssignedClients),
    AssignClientToCollection: canAssignClient ? { detailsMenu: 'enabled' } : {},
    AddTextBlock: { detailsMenu: 'enabled' },
    EditInsightConfigItems: { detailsButton: 'enabled' },
    MultiEdit: { detailsMenu: 'enabled' },
  };
};

const getViewerInteractions = (
  collection?: Pick<CollectionListItem, 'accessLevel' | 'type'>,
  user?: User
): AvailableInteractions => {
  const isTemplateAdminUser = isTemplateAdmin(user);
  return {
    EditCollection: {},
    DuplicateCollection:
      collection.type === CollectionType.Collection ? { overviewMenu: 'enabled', detailsButton: 'enabled' } : {},
    CreateTemplate: getCreateTemplateInteraction(isTemplateAdminUser, collection.type),
    DeleteCollection: {},
    RemovePermissions:
      collection.type === CollectionType.Collection ? { overviewMenu: 'enabled', detailsButton: 'enabled' } : {},
    ExportCollection: { detailsButton: 'enabled' },
    EditInsight: {},
    DeleteInsight: {},
    ExportInsight: { detailsButton: 'enabled' },
    CopyInsight: { detailsButton: 'enabled' },
    ShareCollection: {},
    AssignClientToCollection: {},
    AddTextBlock: {},
    EditInsightConfigItems: {},
    MultiEdit: {},
  };
};

export const getAvailableInteractions = (
  collection?: Pick<CollectionListItem, 'accessLevel' | 'assignedClients' | 'type'>,
  user?: User,
  clients?: Client[],
  editModeEnabled?: boolean
): AvailableInteractions => {
  let interactions: AvailableInteractions;

  if (collection?.accessLevel === AccessLevel.Owner) {
    interactions = getOwnerInteractions(collection, user, clients);
  } else if (collection?.accessLevel === AccessLevel.Viewer) {
    interactions = getViewerInteractions(collection, user);
  } else {
    interactions = buildEmptyInteractions();
  }

  // Disable all interactions when in edit mode
  return editModeEnabled ? disableAllInteractions(interactions) : interactions;
};
